import React, { FC, useState } from 'react';
import getClassName from 'src/common/utils/getClassName';
import { MOVED_PAGES } from 'src/common/constants';
import classNames from 'classnames';
import styles from './TripDetailsCard.module.scss';
import UniversalLink from '../UniversalLink';
import EditButton from './EditButton';
import BoatPlaceholderImage from '../BoatPlaceholderImage';

export type Props = {
  listingId?: string;
  headline?: string;
  listingPhoto?: string;
  variant?: 'default' | 'white';
  titleLayout?: 'column' | 'row';
  canEdit?: boolean;
  departureDate: string;
  returnDate: string;
  departureTime: string;
  returnTime: string;
  alternateDepartDate?: string;
  alternateReturnDate?: string;
  duration: string;
  capacity: string;
  captainOption: string;
  onListingEdit?: () => void;
  onDateEdit?: () => void;
  onAlternateDateEdit?: () => void;
  onTimeEdit?: () => void;
  onDurationEdit?: () => void;
  onCapacityEdit?: () => void;
  onCaptainEdit?: () => void;
};

const TripDetailsCard: FC<Props> = ({
  listingId,
  variant = 'white',
  titleLayout = 'column',
  listingPhoto,
  headline,
  canEdit = false,
  departureDate,
  returnDate,
  departureTime,
  returnTime,
  alternateDepartDate,
  alternateReturnDate,
  duration,
  capacity,
  captainOption,
  onListingEdit = () => {},
  onDateEdit = () => {},
  onAlternateDateEdit = () => {},
  onTimeEdit = () => {},
  onDurationEdit = () => {},
  onCapacityEdit = () => {},
  onCaptainEdit = () => {},
}) => {
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const hasAlternateDates = alternateDepartDate && alternateReturnDate;
  const hasHeadline = headline || listingId;
  const hasListingPhoto = listingPhoto;

  return (
    <div className={getClassName(styles, 'root', variant)}>
      {(hasHeadline || hasListingPhoto)
      && (
        <>
          <h4 className={styles.header}>Trip Details</h4>
          <div className={getClassName(styles, 'titleContainer', titleLayout)}>
            {hasListingPhoto && !imageLoadFailed && (
              <img
                src={listingPhoto}
                alt={headline}
                onError={() => setImageLoadFailed(true)}
                className={getClassName(styles, 'listingImage', titleLayout)}
              />
            )}
            {hasListingPhoto && imageLoadFailed && (
              <div className={getClassName(styles, 'placeholder', titleLayout)}>
                <BoatPlaceholderImage classNameModifier="listing" />
              </div>
            )}
            {hasHeadline && (
              <div className={styles.headlineContainer}>
                <div className={styles.headlineWrapper}>
                  {headline && <span className={getClassName(styles, 'headline', titleLayout)}>{headline}</span>}
                  {listingId && (
                    <UniversalLink
                      className={styles.viewListingLink}
                      to={`${MOVED_PAGES.LISTING}${listingId}`}
                    >
                      View listing
                    </UniversalLink>
                  )}
                </div>
                {canEdit && (
                  <EditButton onClick={onListingEdit} />
                )}
              </div>
            )}
          </div>
        </>
      )}
      <div className={getClassName(styles, 'dateTimeContainer', classNames({ showDottedBorder: !hasAlternateDates }))}>
        <span className={styles.departLabel}>Departure</span>
        <span className={`${styles.returnLabel} ${styles.tripDetailsHeading}`}>Return</span>
        <span className={styles.departTime}>{departureTime}</span>
        <div className={styles.returnTime}>
          <span>{returnTime}</span>
          {canEdit && (
            <EditButton onClick={onTimeEdit} />
          )}
        </div>
        <span className={styles.departDate}>{departureDate}</span>
        <div className={styles.returnDate}>
          <span>{returnDate}</span>
          {canEdit && (
            <EditButton onClick={onDateEdit} />
          )}
        </div>
      </div>
      {hasAlternateDates && (
        <>
          <span className={`${styles.alternateDatesLabel} ${styles.tripDetailsHeading}`}>Alternate Dates</span>
          <div className={getClassName(styles, 'dateTimeContainer', classNames({ showDottedBorder: hasAlternateDates }))}>
            <span className={getClassName(styles, 'departLabel', classNames({ smallFonts: hasAlternateDates }))}>Departure</span>
            <span className={getClassName(styles, 'returnLabel', classNames({ smallFonts: hasAlternateDates }))}>Return</span>
            <span className={styles.departDate}>{departureDate}</span>
            <div className={styles.returnDate}>
              <span>{returnDate}</span>
              {canEdit && (
                <EditButton onClick={onAlternateDateEdit} />
              )}
            </div>
          </div>
        </>
      )}
      <div className={styles.durationCapacityContainer}>
        <div className={styles.durationContainer}>
          <span className={styles.durationLabel}>Duration</span>
          <div className={styles.duration}>
            <span className={styles.durationValue}>{duration}</span>
            {canEdit && (
              <EditButton onClick={onDurationEdit} />
            )}
          </div>
        </div>
        <div className={styles.capacityContainer}>
          <span className={styles.capacityLabel}>Capacity</span>
          <div className={styles.capacityValue}>
            <span>{capacity}</span>
            {canEdit && (
              <EditButton onClick={onCapacityEdit} />
            )}
          </div>
        </div>
      </div>
      <div className={styles.captainContainer}>
        <span className={styles.captainLabel}>{captainOption}</span>
        {canEdit && (
          <EditButton onClick={onCaptainEdit} />
        )}
      </div>
    </div>
  );
};

export default TripDetailsCard;
